import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AlertTriangle,
  ArrowLeft,
  Building2,
  Check,
  ChevronRight,
  Download,
  ExternalLink,
  FileText,
  Loader2,
  LogOut,
  MapPin,
  Plus,
  RefreshCw,
  Save,
  Search,
  Sparkles,
  Trash2,
  X
} from "lucide-react";
import "./valuation.css";

// ---------------------------------------------------------------------------
// Utilidades
// ---------------------------------------------------------------------------

const BASE_PATH = "/admin/valoraciones";
const PROCESSING = new Set(["processing", "searching", "calculating", "writing"]);

const PROPERTY_TYPES = [
  ["piso", "Piso"],
  ["atico", "Ático"],
  ["duplex", "Dúplex"],
  ["bajo", "Bajo"],
  ["estudio", "Estudio"],
  ["casa", "Casa / chalet"]
];

const CONDITIONS = [
  ["reformar", "A reformar"],
  ["actualizar", "Para actualizar"],
  ["buen_estado", "Buen estado"],
  ["reformado", "Reformado"],
  ["nuevo", "A estrenar"]
];
const CONDITION_LABELS = Object.fromEntries(CONDITIONS);

const HEATING = ["", "Central", "Individual gas", "Individual eléctrica", "Aerotermia / bomba de calor", "Gasóleo", "Sin calefacción"];
const ORIENTATIONS = ["", "Norte", "Sur", "Este", "Oeste", "Noreste", "Noroeste", "Sureste", "Suroeste", "Varias"];
const ENERGY = ["", "A", "B", "C", "D", "E", "F", "G"];

const numberFormat = new Intl.NumberFormat("es-ES", { maximumFractionDigits: 0, useGrouping: "always" });
const euro = (value) => (value === null || value === undefined || value === "" ? "—" : `${numberFormat.format(Number(value))} €`);
const num = (value) => (value === null || value === undefined || value === "" ? "—" : numberFormat.format(Number(value)));
const dateTime = (value) =>
  new Intl.DateTimeFormat("es-ES", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }).format(
    new Date(value)
  );

async function apiRequest(token, path, { method = "GET", body } = {}) {
  const response = await fetch(path, {
    method,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(body ? { "Content-Type": "application/json" } : {})
    },
    body: body ? JSON.stringify(body) : undefined
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.message || `Error ${response.status}`);
  return data;
}

function useRoute() {
  const [path, setPath] = useState(window.location.pathname);
  useEffect(() => {
    const onPop = () => setPath(window.location.pathname);
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);
  const navigate = useCallback((next) => {
    window.history.pushState({}, "", next);
    setPath(next);
    window.scrollTo(0, 0);
  }, []);
  return [path, navigate];
}

// Planta del Catastro ("01", "00", "-1", "EN"…) → número de planta.
function catastroFloorNumber(value) {
  const raw = String(value || "").trim().toUpperCase();
  if (!raw) return "";
  if (["BJ", "00", "0", "PB"].includes(raw)) return 0;
  if (["EN", "EP", "ENT"].includes(raw)) return 0;
  if (["SS", "ST"].includes(raw)) return -1;
  const parsed = Number(raw.replace("+", ""));
  return Number.isFinite(parsed) ? parsed : "";
}

function floorLabel(value) {
  const raw = String(value || "").trim().toUpperCase();
  if (!raw) return "";
  if (["BJ", "00", "0", "PB"].includes(raw)) return "Bajo";
  if (["EN", "EP", "ENT"].includes(raw)) return "Entreplanta";
  if (["SS", "ST"].includes(raw)) return "Semisótano";
  if (raw === "AT") return "Ático";
  const parsed = Number(raw.replace("+", ""));
  if (!Number.isFinite(parsed)) return raw;
  if (parsed < 0) return `Sótano ${Math.abs(parsed)}`;
  return `${parsed}º`;
}

function doorLabel(value) {
  const raw = String(value || "").trim().toUpperCase();
  const map = { DR: "Dcha", DCHA: "Dcha", IZ: "Izda", IZDA: "Izda", CT: "Centro", CN: "Centro", "01": "", "02": "" };
  if (raw in map) return map[raw];
  return raw.replace(/^0+(?=.)/, "");
}

const STREET_TYPES = {
  CL: "Calle",
  AV: "Avenida",
  PZ: "Plaza",
  PS: "Paseo",
  CR: "Carretera",
  CM: "Camino",
  RD: "Ronda",
  TR: "Travesía",
  GL: "Glorieta",
  UR: "Urbanización",
  PJ: "Pasaje",
  CJ: "Callejón",
  PG: "Polígono",
  BO: "Barrio"
};
const LOWER_WORDS = new Set(["de", "del", "la", "las", "los", "el", "y", "e"]);

function titleCase(text) {
  return String(text || "")
    .toLowerCase()
    .split(/\s+/)
    .map((word, index) => (index > 0 && LOWER_WORDS.has(word) ? word : word.charAt(0).toUpperCase() + word.slice(1)))
    .join(" ");
}

// "AVENIDA PORTUGAL, DE" → "Avenida de Portugal"; "CL TORO" → "Calle Toro".
function formatStreet(raw) {
  let text = String(raw || "").trim();
  const [first, ...rest] = text.split(/\s+/);
  if (STREET_TYPES[first?.toUpperCase()]) text = [STREET_TYPES[first.toUpperCase()], ...rest].join(" ");
  const particle = text.match(/^(\S+)\s+(.*),\s*(DE LA|DE LOS|DE LAS|DEL|DE|LA|EL|LOS|LAS)$/i);
  if (particle) text = `${particle[1]} ${particle[3]} ${particle[2]}`;
  return titleCase(text);
}

function buildAddressLabel(location, unit) {
  const street = formatStreet(location?.street || unit?.street || "");
  const number = location?.portalNumber ?? unit?.number ?? "";
  const interior = unit ? [floorLabel(unit.floor), doorLabel(unit.door)].filter(Boolean).join(" ") : "";
  return [`${street} ${number}`.trim(), interior].filter(Boolean).join(", ");
}

const EMPTY_SUBJECT = {
  address: { label: "", street: "", number: "", municipality: "", province: "", provinceCode: "", postalCode: "", lat: null, lng: null },
  refCatastral: "",
  catastro: null,
  propertyType: "piso",
  builtArea: "",
  usableArea: "",
  rooms: "",
  bathrooms: "",
  floor: "",
  hasLift: null,
  exterior: true,
  condition: "buen_estado",
  yearBuilt: "",
  garage: false,
  storage: false,
  terrace: false,
  pool: false,
  airConditioning: false,
  heating: "",
  orientation: "",
  energyRating: "",
  agentNotes: "",
  client: { name: "", phone: "", email: "" }
};

// ---------------------------------------------------------------------------
// Contenedor
// ---------------------------------------------------------------------------

export function ValuationsAdmin({ token, user, sidebar, onLogout }) {
  const [path, navigate] = useRoute();
  const [toast, setToast] = useState("");

  useEffect(() => {
    if (!toast) return undefined;
    const timer = setTimeout(() => setToast(""), 5000);
    return () => clearTimeout(timer);
  }, [toast]);

  const sub = path.replace(BASE_PATH, "").replace(/^\//, "");
  let view;
  if (sub === "nueva") {
    view = <NewValuation token={token} navigate={navigate} notify={setToast} />;
  } else if (/^\d+$/.test(sub)) {
    view = <ValuationDetail key={sub} id={sub} token={token} user={user} navigate={navigate} notify={setToast} />;
  } else {
    view = <ValuationList token={token} navigate={navigate} notify={setToast} />;
  }

  return (
    <main className="admin-dashboard">
      {sidebar}
      <section className="admin-main val-main">
        <header className="admin-topbar">
          <div>
            <p>Herramientas</p>
            <h1>Valoraciones</h1>
          </div>
          <div className="admin-topbar-actions">
            {sub !== "nueva" && (
              <button type="button" onClick={() => navigate(`${BASE_PATH}/nueva`)}>
                <Plus size={16} />
                Nueva valoración
              </button>
            )}
            <button type="button" onClick={onLogout}>
              <LogOut size={16} />
              Salir
            </button>
          </div>
        </header>
        {view}
      </section>
      {toast && <div className="admin-toast">{toast}</div>}
    </main>
  );
}

// ---------------------------------------------------------------------------
// Listado
// ---------------------------------------------------------------------------

function ValuationList({ token, navigate, notify }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    apiRequest(token, "/api/admin/valuations")
      .then(setData)
      .catch((error) => notify(error.message));
  }, [token, notify]);

  if (!data) return <div className="val-loading"><Loader2 className="spin" size={18} /> Cargando valoraciones…</div>;

  return (
    <>
      {(!data.services.apify || !data.services.ai) && (
        <div className="val-alert">
          <AlertTriangle size={16} />
          {!data.services.apify && "Falta configurar APIFY_TOKEN en el servidor. "}
          {!data.services.ai && "Falta configurar OPENAI_API_KEY en el servidor (sin IA no se redactan los textos)."}
        </div>
      )}
      <section className="admin-panel-section">
        <div className="admin-section-heading">
          <div>
            <p>Informes</p>
            <h2>Valoraciones realizadas</h2>
          </div>
          <span>{data.valuations.length}</span>
        </div>
        {data.valuations.length ? (
          <div className="val-list">
            {data.valuations.map((item) => (
              <button key={item.id} type="button" className="val-list-row" onClick={() => navigate(`${BASE_PATH}/${item.id}`)}>
                <div>
                  <strong>{item.address || "Sin dirección"}</strong>
                  <span>
                    {[item.client_name, item.built_area ? `${num(item.built_area)} m²` : null, item.agent_name].filter(Boolean).join(" · ")}
                  </span>
                </div>
                <div className="val-list-value">
                  <strong>{item.market_value ? euro(item.market_value) : "—"}</strong>
                  <span>{dateTime(item.created_at)}</span>
                </div>
                <StatusPill status={item.status} />
                <ChevronRight size={16} />
              </button>
            ))}
          </div>
        ) : (
          <div className="admin-empty val-empty">
            <FileText size={22} />
            <p>Todavía no hay valoraciones.</p>
            <button type="button" className="val-btn val-btn-primary" onClick={() => navigate(`${BASE_PATH}/nueva`)}>
              <Plus size={16} /> Crear la primera
            </button>
          </div>
        )}
      </section>
    </>
  );
}

function StatusPill({ status }) {
  const labels = {
    done: "Lista",
    error: "Error",
    searching: "Buscando testigos",
    calculating: "Calculando",
    writing: "Redactando",
    processing: "En proceso"
  };
  return <span className={`val-pill val-pill-${PROCESSING.has(status) ? "busy" : status}`}>{labels[status] || status}</span>;
}

// ---------------------------------------------------------------------------
// Nueva valoración
// ---------------------------------------------------------------------------

function NewValuation({ token, navigate, notify }) {
  const [subject, setSubject] = useState(EMPTY_SUBJECT);
  const [location, setLocation] = useState(null);
  const [building, setBuilding] = useState(null);
  const [unitRef, setUnitRef] = useState("");
  const [energy, setEnergy] = useState(null);
  // Paso de la consulta al Catastro en curso: "building" (viviendas de la
  // finca), "unit" (ficha de una vivienda) o null.
  const [catastroStep, setCatastroStep] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const update = (patch) => setSubject((current) => ({ ...current, ...patch }));

  async function selectLocation(result) {
    setCatastroStep("building");
    setLocation(result);
    setBuilding(null);
    setUnitRef("");
    setEnergy(null);
    update({
      address: {
        ...EMPTY_SUBJECT.address,
        label: buildAddressLabel(result, null),
        street: formatStreet(result.street),
        number: String(result.portalNumber ?? ""),
        municipality: titleCase(result.municipality),
        province: titleCase(result.province),
        provinceCode: result.provinceCode,
        postalCode: result.postalCode,
        lat: result.lat,
        lng: result.lng
      },
      refCatastral: result.refCatastral || "",
      catastro: null
    });

    try {
      const query = result.refCatastral ? `ref=${result.refCatastral}` : `lat=${result.lat}&lng=${result.lng}`;
      const data = await apiRequest(token, `/api/admin/valuations/catastro?${query}`);
      setBuilding(data);
      const residential = data.units.filter((unit) => /residencial/i.test(unit.use));
      if (data.units.length === 1) await selectUnit(data.units[0], result);
      else if (residential.length === 1) await selectUnit(residential[0], result);
    } catch (error) {
      setBuilding({ error: error.message, units: [] });
    } finally {
      setCatastroStep(null);
    }
  }

  async function selectUnit(unit, currentLocation = location) {
    setUnitRef(unit.refCatastral);
    setCatastroStep("unit");
    try {
      const data = await apiRequest(token, `/api/admin/valuations/catastro?ref=${unit.refCatastral}`);
      const detail = data.unit;
      setEnergy(data.energy);
      const floor = catastroFloorNumber(detail.floor);
      const isHouse = detail.isSingleFamily;
      setSubject((current) => ({
        ...current,
        address: {
          ...current.address,
          label: buildAddressLabel(currentLocation, detail),
          postalCode: current.address.postalCode || detail.postalCode,
          lat: current.address.lat ?? data.coordinates?.lat ?? null,
          lng: current.address.lng ?? data.coordinates?.lng ?? null
        },
        refCatastral: detail.refCatastral,
        catastro: {
          use: detail.use,
          builtArea: detail.builtArea,
          livingArea: detail.livingArea,
          yearBuilt: detail.yearBuilt,
          parcelType: detail.parcelType,
          parcelArea: detail.parcelArea,
          constructions: detail.constructions,
          floor: detail.floor,
          door: detail.door
        },
        propertyType: isHouse ? "casa" : floor === 0 ? "bajo" : current.propertyType === "casa" || current.propertyType === "bajo" ? "piso" : current.propertyType,
        builtArea: detail.livingArea || detail.builtArea || current.builtArea,
        floor: isHouse ? "" : floor,
        yearBuilt: detail.yearBuilt || current.yearBuilt,
        garage: current.garage || detail.hasGarageInCatastro,
        storage: current.storage || detail.hasStorageInCatastro,
        energyRating: data.energy?.rating && ENERGY.includes(data.energy.rating) ? data.energy.rating : current.energyRating
      }));
    } catch (error) {
      notify(error.message);
    } finally {
      setCatastroStep(null);
    }
  }

  async function submit(event) {
    event.preventDefault();
    if (!subject.address.lat) return notify("Busca y selecciona la dirección del inmueble.");
    if (!Number(subject.builtArea)) return notify("Indica la superficie construida.");
    setSubmitting(true);
    try {
      const data = await apiRequest(token, "/api/admin/valuations", { method: "POST", body: { subject } });
      navigate(`${BASE_PATH}/${data.valuation.id}`);
    } catch (error) {
      notify(error.message);
      setSubmitting(false);
    }
  }

  const units = building?.units || [];
  const sortedUnits = useMemo(
    () => [...units].sort((a, b) => Number(/residencial/i.test(b.use)) - Number(/residencial/i.test(a.use))),
    [units]
  );

  return (
    <form className="val-form" onSubmit={submit}>
      <button type="button" className="val-back" onClick={() => navigate(BASE_PATH)}>
        <ArrowLeft size={15} /> Volver al listado
      </button>

      <section className="val-card">
        <StepHeading number="1" title="Ubicación del inmueble" text="Busca la dirección; los datos del Catastro se cargan solos." />
        <AddressSearch
          token={token}
          onSelect={selectLocation}
          onLookupStart={() => setCatastroStep("building")}
          onLookupError={(message) => {
            setCatastroStep(null);
            notify(message);
          }}
        />

        {catastroStep && <CatastroLoader step={catastroStep} />}

        {location && (
          <div className="val-location">
            <MapPin size={16} />
            <div>
              <strong>{formatStreet(location.street)} {location.portalNumber ?? ""}</strong>
              <span>
                {[location.postalCode, titleCase(location.municipality), location.refCatastral && `Ref. ${location.refCatastral}`]
                  .filter(Boolean)
                  .join(" · ")}
              </span>
            </div>
          </div>
        )}

        {building?.error && (
          <p className="val-hint val-hint-warn">
            <AlertTriangle size={14} /> {building.error} Puedes rellenar los datos a mano.
          </p>
        )}

        {sortedUnits.length > 1 && (
          <div className="val-units">
            <p className="val-label">Elige la vivienda ({sortedUnits.length} inmuebles en la finca)</p>
            <div className="val-units-grid">
              {sortedUnits.map((unit) => (
                <button
                  key={unit.refCatastral}
                  type="button"
                  className={`val-unit ${unitRef === unit.refCatastral ? "active" : ""} ${/residencial/i.test(unit.use) ? "" : "muted"}`}
                  onClick={() => selectUnit(unit)}
                >
                  <strong>
                    {[unit.stair && unit.stair !== "1" ? `Esc. ${unit.stair}` : "", floorLabel(unit.floor), doorLabel(unit.door)]
                      .filter(Boolean)
                      .join(" · ") || "Inmueble"}
                  </strong>
                  <span>
                    {unit.use} · {num(unit.builtArea)} m²
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        {subject.catastro && (
          <div className="val-catastro">
            <Building2 size={16} />
            <div>
              <strong>Catastro · {subject.refCatastral}</strong>
              <span>
                {[
                  subject.catastro.use,
                  subject.catastro.builtArea && `${num(subject.catastro.builtArea)} m² catastrales`,
                  subject.catastro.livingArea && subject.catastro.livingArea !== subject.catastro.builtArea
                    ? `${num(subject.catastro.livingArea)} m² de vivienda`
                    : null,
                  subject.catastro.yearBuilt && `año ${subject.catastro.yearBuilt}`,
                  energy?.rating && `certificado ${energy.rating} (${energy.date?.slice(0, 4)})`
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </span>
            </div>
          </div>
        )}

        {subject.address.lat && (
          <label className="val-field val-field-wide">
            <span>Dirección que aparecerá en el informe</span>
            <input
              value={subject.address.label}
              onChange={(event) => update({ address: { ...subject.address, label: event.target.value } })}
            />
          </label>
        )}
      </section>

      <section className="val-card">
        <StepHeading number="2" title="Características" text="Revisa lo que viene del Catastro y completa lo que se ve en la visita." />
        <PropertyFields subject={subject} onChange={update} />
      </section>

      <section className="val-card">
        <StepHeading number="3" title="Propietario" text="Opcional. El nombre aparece en la portada del informe." />
        <div className="val-grid">
          <TextField label="Nombre" value={subject.client.name} onChange={(value) => update({ client: { ...subject.client, name: value } })} />
          <TextField label="Teléfono" value={subject.client.phone} onChange={(value) => update({ client: { ...subject.client, phone: value } })} />
          <TextField label="Email" type="email" value={subject.client.email} onChange={(value) => update({ client: { ...subject.client, email: value } })} />
        </div>
      </section>

      <div className="val-submit">
        <p>
          Se buscarán los inmuebles en venta alrededor del piso, se calculará el valor por testigos y la IA redactará el análisis.
          Tarda alrededor de un minuto.
        </p>
        <button type="submit" className="val-btn val-btn-primary val-btn-lg" disabled={submitting}>
          {submitting ? <Loader2 className="spin" size={16} /> : <Sparkles size={16} />}
          Generar valoración
        </button>
      </div>
    </form>
  );
}

const CATASTRO_MESSAGES = {
  building: ["Consultando el Catastro…", "Buscando las viviendas de la finca…", "Leyendo plantas y puertas…"],
  unit: ["Leyendo la ficha de la vivienda…", "Superficies y año de construcción…", "Buscando el certificado energético…"]
};

// Animación mientras se consulta el Catastro (puede tardar varios segundos).
function CatastroLoader({ step }) {
  const messages = CATASTRO_MESSAGES[step] || CATASTRO_MESSAGES.building;
  const [index, setIndex] = useState(0);

  useEffect(() => {
    setIndex(0);
    const timer = setInterval(() => setIndex((current) => Math.min(current + 1, messages.length - 1)), 1600);
    return () => clearInterval(timer);
  }, [step, messages.length]);

  return (
    <div className="val-catastro-loader" role="status" aria-live="polite">
      <div className="val-catastro-loader-icon">
        <Building2 size={20} />
        <span />
      </div>
      <div className="val-catastro-loader-text">
        <strong key={`${step}-${index}`}>{messages[index]}</strong>
        <div className="val-catastro-loader-bar">
          <i />
        </div>
      </div>
    </div>
  );
}

function StepHeading({ number, title, text }) {
  return (
    <div className="val-step">
      <span>{number}</span>
      <div>
        <h2>{title}</h2>
        {text && <p>{text}</p>}
      </div>
    </div>
  );
}

function AddressSearch({ token, onSelect, onLookupStart, onLookupError }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const requestId = useRef(0);
  // Al elegir un resultado escribimos su dirección en la caja: ese cambio no
  // debe lanzar otra búsqueda (volvía a abrir la lista mientras cargaba el Catastro).
  const skipNextSearch = useRef(false);

  useEffect(() => {
    if (skipNextSearch.current) {
      skipNextSearch.current = false;
      return undefined;
    }
    const q = query.trim();
    if (q.length < 4) {
      setResults([]);
      return undefined;
    }
    const current = ++requestId.current;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const isRef = /^[0-9A-Z]{14}([0-9A-Z]{6})?$/i.test(q.replace(/\s+/g, ""));
        if (isRef) {
          setResults([{ id: "ref", label: `Referencia catastral ${q.toUpperCase()}`, refOnly: q.replace(/\s+/g, "").toUpperCase() }]);
        } else {
          const data = await apiRequest(token, `/api/admin/valuations/geocode?q=${encodeURIComponent(q)}`);
          if (current === requestId.current) setResults(data.results);
        }
        if (current === requestId.current) setOpen(true);
      } catch {
        setResults([]);
      } finally {
        if (current === requestId.current) setLoading(false);
      }
    }, 350);
    return () => clearTimeout(timer);
  }, [query, token]);

  async function choose(result) {
    setOpen(false);
    // Invalida cualquier búsqueda en curso para que no reabra la lista.
    requestId.current += 1;
    setLoading(false);
    if (result.refOnly) {
      // Entrada por referencia catastral: pedimos las coordenadas al Catastro.
      onLookupStart?.();
      let data;
      try {
        data = await apiRequest(token, `/api/admin/valuations/catastro?ref=${result.refOnly.slice(0, 14)}`);
      } catch (error) {
        onLookupError?.(error.message);
        return;
      }
      const first = data.units[0] || {};
      skipNextSearch.current = true;
      setQuery(`${first.street || ""} ${first.number || ""}, ${first.municipality || ""}`);
      onSelect({
        id: result.refOnly,
        street: first.street,
        portalNumber: first.number,
        municipality: first.municipality,
        province: first.province,
        provinceCode: first.provinceCode,
        postalCode: first.postalCode,
        refCatastral: result.refOnly.slice(0, 14),
        lat: data.coordinates?.lat,
        lng: data.coordinates?.lng
      });
      return;
    }
    skipNextSearch.current = true;
    setQuery(result.label);
    onSelect(result);
  }

  return (
    <div className="val-search">
      <div className="val-search-input">
        <Search size={16} />
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onFocus={() => results.length && setOpen(true)}
          placeholder="Ej.: Avenida de Portugal 46, Salamanca · o referencia catastral"
          autoComplete="off"
        />
        {loading && <Loader2 className="spin" size={15} />}
      </div>
      {open && results.length > 0 && (
        <div className="val-search-results">
          {results.map((result) => (
            <button key={result.id} type="button" onClick={() => choose(result)}>
              <MapPin size={14} />
              <div>
                <strong>{result.refOnly ? result.label : `${formatStreet(result.street)} ${result.portalNumber ?? ""}`}</strong>
                {!result.refOnly && <span>{[result.postalCode, result.municipality, result.province].filter(Boolean).join(" · ")}</span>}
              </div>
            </button>
          ))}
        </div>
      )}
      {open && !loading && query.trim().length >= 4 && !results.length && (
        <div className="val-search-results val-search-empty">Sin resultados. Prueba sin «calle»/«avenida» o con la referencia catastral.</div>
      )}
    </div>
  );
}

function TextField({ label, value, onChange, type = "text", suffix, placeholder, wide }) {
  return (
    <label className={`val-field ${wide ? "val-field-wide" : ""}`}>
      <span>{label}</span>
      <div className="val-input">
        <input
          type={type}
          value={value ?? ""}
          placeholder={placeholder}
          inputMode={type === "number" ? "decimal" : undefined}
          onChange={(event) => onChange(event.target.value)}
        />
        {suffix && <em>{suffix}</em>}
      </div>
    </label>
  );
}

function SelectField({ label, value, options, onChange }) {
  return (
    <label className="val-field">
      <span>{label}</span>
      <div className="val-input">
        <select value={value ?? ""} onChange={(event) => onChange(event.target.value)}>
          {options.map((option) => {
            const [optionValue, optionLabel] = Array.isArray(option) ? option : [option, option || "—"];
            return (
              <option key={optionValue} value={optionValue}>
                {optionLabel}
              </option>
            );
          })}
        </select>
      </div>
    </label>
  );
}

function Segmented({ label, value, options, onChange }) {
  return (
    <div className="val-field">
      <span>{label}</span>
      <div className="val-segmented">
        {options.map(([optionValue, optionLabel]) => (
          <button
            key={String(optionValue)}
            type="button"
            className={value === optionValue ? "active" : ""}
            onClick={() => onChange(optionValue)}
          >
            {optionLabel}
          </button>
        ))}
      </div>
    </div>
  );
}

function PropertyFields({ subject, onChange }) {
  const isHouse = subject.propertyType === "casa";
  const extras = [
    ["garage", "Garaje incluido"],
    ["storage", "Trastero"],
    ["terrace", "Terraza"],
    ["pool", "Piscina comunitaria"],
    ["airConditioning", "Aire acondicionado"]
  ];
  return (
    <div className="val-fields">
      <div className="val-grid">
        <SelectField label="Tipología" value={subject.propertyType} options={PROPERTY_TYPES} onChange={(value) => onChange({ propertyType: value })} />
        <TextField label="Superficie construida" type="number" suffix="m²" value={subject.builtArea} onChange={(value) => onChange({ builtArea: value })} />
        <TextField label="Superficie útil" type="number" suffix="m²" value={subject.usableArea} onChange={(value) => onChange({ usableArea: value })} />
        <TextField label="Dormitorios" type="number" value={subject.rooms} onChange={(value) => onChange({ rooms: value })} />
        <TextField label="Baños" type="number" value={subject.bathrooms} onChange={(value) => onChange({ bathrooms: value })} />
        {!isHouse && (
          <TextField
            label="Planta"
            type="number"
            value={subject.floor}
            placeholder="0 = bajo"
            onChange={(value) => onChange({ floor: value })}
          />
        )}
        <TextField label="Año de construcción" type="number" value={subject.yearBuilt} onChange={(value) => onChange({ yearBuilt: value })} />
        <SelectField label="Certificado energético" value={subject.energyRating} options={ENERGY} onChange={(value) => onChange({ energyRating: value })} />
      </div>

      <div className="val-grid">
        {!isHouse && (
          <Segmented
            label="Ascensor"
            value={subject.hasLift}
            options={[
              [true, "Sí"],
              [false, "No"]
            ]}
            onChange={(value) => onChange({ hasLift: value })}
          />
        )}
        <Segmented
          label="Orientación"
          value={subject.exterior}
          options={[
            [true, "Exterior"],
            [false, "Interior"]
          ]}
          onChange={(value) => onChange({ exterior: value })}
        />
        <SelectField label="Calefacción" value={subject.heating} options={HEATING} onChange={(value) => onChange({ heating: value })} />
        <SelectField label="Orientación principal" value={subject.orientation} options={ORIENTATIONS} onChange={(value) => onChange({ orientation: value })} />
      </div>

      <Segmented label="Estado de conservación" value={subject.condition} options={CONDITIONS} onChange={(value) => onChange({ condition: value })} />

      <div className="val-field">
        <span>Extras</span>
        <div className="val-checks">
          {extras.map(([key, label]) => (
            <button
              key={key}
              type="button"
              className={subject[key] ? "active" : ""}
              onClick={() => onChange({ [key]: !subject[key] })}
            >
              {subject[key] ? <Check size={14} /> : <Plus size={14} />}
              {label}
            </button>
          ))}
        </div>
      </div>

      <label className="val-field val-field-wide">
        <span>Notas del agente para el análisis</span>
        <textarea
          rows={4}
          value={subject.agentNotes}
          placeholder="Lo que has visto en la visita: calidades, reformas, luz, vistas, ruido, comunidad, derramas, urgencia del propietario…"
          onChange={(event) => onChange({ agentNotes: event.target.value })}
        />
      </label>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Ficha de una valoración
// ---------------------------------------------------------------------------

function ValuationDetail({ id, token, navigate, notify }) {
  const [valuation, setValuation] = useState(null);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [editingSubject, setEditingSubject] = useState(false);
  const [subjectDraft, setSubjectDraft] = useState(null);
  const [settingsDraft, setSettingsDraft] = useState(null);
  const [aiDraft, setAiDraft] = useState(null);
  const [displayDraft, setDisplayDraft] = useState(null);

  const load = useCallback(async () => {
    const data = await apiRequest(token, `/api/admin/valuations/${id}`);
    setValuation(data.valuation);
    return data.valuation;
  }, [id, token]);

  // Cada borrador editable se reinicia solo cuando cambia su parte en el
  // servidor (excluir un testigo no debe pisar textos sin guardar).
  const optionsKey = valuation ? JSON.stringify(valuation.options) : "";
  const aiKey = valuation ? `${valuation.ai?.generatedAt || ""}|${valuation.ai?.editedAt || ""}` : "";
  const displayKey = valuation ? JSON.stringify(valuation.display) : "";

  useEffect(() => {
    if (!valuation) return;
    setSettingsDraft({
      negotiationPct: valuation.options.negotiationPct ?? "",
      aiAdjustmentPct: valuation.options.aiAdjustmentPct ?? 0,
      manualValue: valuation.options.manualValue ?? "",
      manualListingPrice: valuation.options.manualListingPrice ?? "",
      garageValue: valuation.options.garageValue ?? ""
    });
  }, [optionsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (valuation) setAiDraft(valuation.ai ? { ...valuation.ai } : null);
  }, [aiKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (valuation) setDisplayDraft({ ...valuation.display });
  }, [displayKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    load().catch((error) => notify(error.message));
  }, [load, notify]);

  // Mientras se procesa, consultamos el estado cada pocos segundos.
  useEffect(() => {
    if (!valuation || !PROCESSING.has(valuation.status)) return undefined;
    const timer = setTimeout(() => load().catch(() => {}), 2500);
    return () => clearTimeout(timer);
  }, [valuation, load]);

  async function patch(body, message = "Cambios guardados") {
    setSaving(true);
    try {
      const data = await apiRequest(token, `/api/admin/valuations/${id}`, { method: "PATCH", body });
      setValuation(data.valuation);
      if (message) notify(message);
      return data.valuation;
    } catch (error) {
      notify(error.message);
      return null;
    } finally {
      setSaving(false);
    }
  }

  async function trigger(action, body) {
    try {
      const data = await apiRequest(token, `/api/admin/valuations/${id}/${action}`, { method: "POST", body: body || {} });
      setValuation(data.valuation);
    } catch (error) {
      notify(error.message);
    }
  }

  async function remove() {
    if (!window.confirm("¿Eliminar esta valoración? No se puede deshacer.")) return;
    try {
      await apiRequest(token, `/api/admin/valuations/${id}`, { method: "DELETE" });
      navigate(BASE_PATH);
    } catch (error) {
      notify(error.message);
    }
  }

  const aiDirty = useMemo(() => {
    if (!aiDraft || !valuation?.ai) return false;
    return ["resumen", "analisisZona", "analisisMercado", "justificacionPrecio", "estrategia", "plazoVenta", "puntosFuertes", "puntosMejora"].some(
      (key) => JSON.stringify(aiDraft[key]) !== JSON.stringify(valuation.ai[key])
    );
  }, [aiDraft, valuation]);

  const displayDirty = useMemo(
    () => displayDraft && valuation && JSON.stringify(displayDraft) !== JSON.stringify(valuation.display),
    [displayDraft, valuation]
  );

  async function downloadPdf(preview = false) {
    setDownloading(true);
    try {
      if (aiDirty || displayDirty) {
        await patch({ ...(aiDirty ? { ai: aiDraft } : {}), ...(displayDirty ? { display: displayDraft } : {}) }, null);
      }
      const response = await fetch(`/api/admin/valuations/${id}/pdf`, { headers: { Authorization: `Bearer ${token}` } });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.message || "No se pudo generar el PDF.");
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      if (preview) {
        window.open(url, "_blank", "noopener");
      } else {
        const disposition = response.headers.get("Content-Disposition") || "";
        const link = document.createElement("a");
        link.href = url;
        link.download = disposition.match(/filename="([^"]+)"/)?.[1] || `valoracion-${id}.pdf`;
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (error) {
      notify(error.message);
    } finally {
      setDownloading(false);
    }
  }

  if (!valuation) return <div className="val-loading"><Loader2 className="spin" size={18} /> Cargando valoración…</div>;

  const { result, subject } = valuation;
  const busy = PROCESSING.has(valuation.status);
  const excludedIds = new Set(valuation.options.excludedIds || []);

  function toggleComparable(itemId) {
    const next = new Set(excludedIds);
    if (next.has(itemId)) next.delete(itemId);
    else next.add(itemId);
    patch({ options: { excludedIds: [...next] } }, null);
  }

  return (
    <div className="val-detail">
      <button type="button" className="val-back" onClick={() => navigate(BASE_PATH)}>
        <ArrowLeft size={15} /> Volver al listado
      </button>

      <section className="val-hero">
        <div>
          <p>
            {PROPERTY_TYPES.find(([key]) => key === subject.propertyType)?.[1]} · {num(subject.builtArea)} m² ·{" "}
            {subject.address.municipality}
          </p>
          <h2>{subject.address.label}</h2>
          <span>
            {[subject.client?.name && `Propietario: ${subject.client.name}`, valuation.agent?.name && `Agente: ${valuation.agent.name}`, dateTime(valuation.createdAt)]
              .filter(Boolean)
              .join(" · ")}
          </span>
        </div>
        <div className="val-hero-actions">
          <StatusPill status={valuation.status} />
          <button type="button" className="val-btn" disabled={busy || !result?.ok || downloading} onClick={() => downloadPdf(true)}>
            <FileText size={15} /> Vista previa
          </button>
          <button type="button" className="val-btn val-btn-primary" disabled={busy || !result?.ok || downloading} onClick={() => downloadPdf(false)}>
            {downloading ? <Loader2 className="spin" size={15} /> : <Download size={15} />} Descargar PDF
          </button>
        </div>
      </section>

      {busy && <ProgressPanel status={valuation.status} message={valuation.statusMessage} />}

      {valuation.status === "error" && (
        <div className="val-alert">
          <AlertTriangle size={16} />
          <span>{valuation.statusMessage || "Error al generar la valoración."}</span>
          <button type="button" className="val-btn" onClick={() => trigger("refresh")}>
            <RefreshCw size={14} /> Reintentar
          </button>
        </div>
      )}

      {result && !result.ok && valuation.status === "done" && (
        <div className="val-alert">
          <AlertTriangle size={16} /> {result.message}
        </div>
      )}

      {result?.ok && (
        <>
          <ResultSummary result={result} search={valuation.search} />

          {valuation.ai?.notaInterna && (
            <section className="val-internal">
              <div>
                <Sparkles size={16} />
                <strong>Nota interna de la IA para el agente</strong>
                <em>No aparece en el PDF</em>
              </div>
              <p>{valuation.ai.notaInterna}</p>
            </section>
          )}

          {settingsDraft && (
            <section className="val-card">
              <div className="val-card-heading">
                <h3>Ajustes del agente</h3>
                <p>Recalcula al instante con los mismos testigos.</p>
              </div>
              <div className="val-grid">
                <TextField
                  label="Margen de negociación"
                  type="number"
                  suffix="%"
                  placeholder={`Auto: ${result.negotiationPct}`}
                  value={settingsDraft.negotiationPct}
                  onChange={(value) => setSettingsDraft({ ...settingsDraft, negotiationPct: value })}
                />
                <TextField
                  label="Ajuste cualitativo (IA)"
                  type="number"
                  suffix="%"
                  value={settingsDraft.aiAdjustmentPct}
                  onChange={(value) => setSettingsDraft({ ...settingsDraft, aiAdjustmentPct: value })}
                />
                <TextField
                  label="Valor de una plaza de garaje"
                  type="number"
                  suffix="€"
                  placeholder={num(result.settings.garageValue)}
                  value={settingsDraft.garageValue}
                  onChange={(value) => setSettingsDraft({ ...settingsDraft, garageValue: value })}
                />
                <TextField
                  label="Fijar valor de mercado a mano"
                  type="number"
                  suffix="€"
                  placeholder={num(result.computedMarketValue)}
                  value={settingsDraft.manualValue}
                  onChange={(value) => setSettingsDraft({ ...settingsDraft, manualValue: value })}
                />
                <TextField
                  label="Fijar precio de salida a mano"
                  type="number"
                  suffix="€"
                  placeholder={num(result.computedListingPrice ?? result.listingPrice)}
                  value={settingsDraft.manualListingPrice}
                  onChange={(value) => setSettingsDraft({ ...settingsDraft, manualListingPrice: value })}
                />
              </div>
              {valuation.ai?.motivoAjuste && Number(valuation.ai.ajustePct) !== 0 && (
                <p className="val-hint">
                  <Sparkles size={14} /> La IA propuso {valuation.ai.ajustePct > 0 ? "+" : ""}
                  {valuation.ai.ajustePct} %: {valuation.ai.motivoAjuste}
                </p>
              )}
              <div className="val-card-actions">
                <button
                  type="button"
                  className="val-btn val-btn-primary"
                  disabled={saving}
                  onClick={() =>
                    patch({
                      options: {
                        negotiationPct: settingsDraft.negotiationPct,
                        aiAdjustmentPct: settingsDraft.aiAdjustmentPct === "" ? 0 : settingsDraft.aiAdjustmentPct,
                        manualValue: settingsDraft.manualValue,
                        manualListingPrice: settingsDraft.manualListingPrice,
                        garageValue: settingsDraft.garageValue
                      }
                    }, "Valoración recalculada")
                  }
                >
                  {saving ? <Loader2 className="spin" size={15} /> : <RefreshCw size={15} />} Recalcular
                </button>
              </div>
            </section>
          )}

          <ComparablesTable result={result} excludedIds={excludedIds} onToggle={toggleComparable} disabled={saving} />
        </>
      )}

      {!busy && (
        <section className="val-card">
          <div className="val-card-heading">
            <h3>Datos del inmueble</h3>
            <button
              type="button"
              className="val-btn"
              onClick={() => {
                setSubjectDraft(subject);
                setEditingSubject(!editingSubject);
              }}
            >
              {editingSubject ? <X size={15} /> : <FileText size={15} />} {editingSubject ? "Cancelar" : "Editar datos"}
            </button>
          </div>
          {editingSubject && subjectDraft ? (
            <>
              <PropertyFields subject={subjectDraft} onChange={(patchValue) => setSubjectDraft({ ...subjectDraft, ...patchValue })} />
              <div className="val-grid">
                <TextField
                  label="Dirección en el informe"
                  wide
                  value={subjectDraft.address.label}
                  onChange={(value) => setSubjectDraft({ ...subjectDraft, address: { ...subjectDraft.address, label: value } })}
                />
                <TextField
                  label="Propietario"
                  value={subjectDraft.client?.name}
                  onChange={(value) => setSubjectDraft({ ...subjectDraft, client: { ...subjectDraft.client, name: value } })}
                />
              </div>
              <div className="val-card-actions">
                <p className="val-hint">Tras cambiar datos importantes, regenera el análisis de IA para que los textos cuadren.</p>
                <button
                  type="button"
                  className="val-btn val-btn-primary"
                  disabled={saving}
                  onClick={async () => {
                    const saved = await patch({ subject: subjectDraft }, "Datos guardados y valoración recalculada");
                    if (saved) setEditingSubject(false);
                  }}
                >
                  <Save size={15} /> Guardar y recalcular
                </button>
              </div>
            </>
          ) : (
            <SubjectSummary subject={subject} />
          )}
        </section>
      )}

      {result?.ok && !busy && (
        <AiEditor
          ai={aiDraft}
          aiError={valuation.aiError}
          dirty={aiDirty}
          saving={saving}
          onChange={setAiDraft}
          onSave={() => patch({ ai: aiDraft }, "Textos guardados")}
          onRegenerate={() => {
            if (aiDirty && !window.confirm("Se perderán los cambios de texto sin guardar. ¿Regenerar el análisis?")) return;
            trigger("ai");
          }}
        />
      )}

      {result?.ok && !busy && displayDraft && (
        <section className="val-card">
          <div className="val-card-heading">
            <h3>Opciones del PDF</h3>
            <p>Se guardan automáticamente al descargar.</p>
          </div>
          <div className="val-grid">
            <TextField label="Agente que firma" value={displayDraft.agentName ?? valuation.agent?.name ?? ""} onChange={(value) => setDisplayDraft({ ...displayDraft, agentName: value })} />
            <TextField label="Teléfono de contacto" placeholder="629 160 038" value={displayDraft.agentPhone ?? ""} onChange={(value) => setDisplayDraft({ ...displayDraft, agentPhone: value })} />
            <TextField label="Email de contacto" placeholder="amelia@inmobiliariahapax.com" value={displayDraft.agentEmail ?? ""} onChange={(value) => setDisplayDraft({ ...displayDraft, agentEmail: value })} />
          </div>
          <div className="val-checks">
            <button
              type="button"
              className={!displayDraft.hideComparables ? "active" : ""}
              onClick={() => setDisplayDraft({ ...displayDraft, hideComparables: !displayDraft.hideComparables })}
            >
              {!displayDraft.hideComparables ? <Check size={14} /> : <Plus size={14} />} Incluir tabla de testigos
            </button>
            <button
              type="button"
              className={!displayDraft.hideListingPrice ? "active" : ""}
              onClick={() => setDisplayDraft({ ...displayDraft, hideListingPrice: !displayDraft.hideListingPrice })}
            >
              {!displayDraft.hideListingPrice ? <Check size={14} /> : <Plus size={14} />} Mostrar precio de salida recomendado
            </button>
          </div>
        </section>
      )}

      {!busy && (
        <div className="val-footer-actions">
          <button type="button" className="val-btn" onClick={() => trigger("refresh")}>
            <RefreshCw size={15} /> Volver a buscar testigos
          </button>
          <button type="button" className="val-btn val-btn-danger" onClick={remove}>
            <Trash2 size={15} /> Eliminar valoración
          </button>
        </div>
      )}
    </div>
  );
}

function ProgressPanel({ status, message }) {
  const steps = [
    ["searching", "Buscando inmuebles en venta en la zona"],
    ["calculating", "Homogeneizando testigos y calculando el valor"],
    ["writing", "Redactando el análisis con IA"]
  ];
  const currentIndex = steps.findIndex(([key]) => key === status);
  return (
    <section className="val-progress">
      <Loader2 className="spin" size={20} />
      <div>
        <strong>{message || "Procesando…"}</strong>
        <ol>
          {steps.map(([key, label], index) => (
            <li key={key} className={index < currentIndex ? "done" : index === currentIndex ? "current" : ""}>
              {index < currentIndex ? <Check size={13} /> : <span />}
              {label}
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

function ResultSummary({ result, search }) {
  const span = Math.max(1, result.range.high - result.range.low);
  const marker = Math.min(100, Math.max(0, ((result.marketValue - result.range.low) / span) * 100));
  return (
    <section className="val-result">
      <div className="val-result-main">
        <p>Valor de mercado estimado</p>
        <strong>{euro(result.marketValue)}</strong>
        <span>
          Horquilla {euro(result.range.low)} – {euro(result.range.high)}
          {result.settings.manualValue ? " · fijado a mano" : ""}
        </span>
        <div className="val-range">
          <div style={{ width: `${marker}%` }} />
          <i style={{ left: `${marker}%` }} />
        </div>
      </div>
      <div className="val-result-kpis">
        <article>
          <span>Precio de salida</span>
          <strong>{euro(result.listingPrice)}</strong>
          {result.settings.manualListingPrice ? <small>fijado a mano</small> : null}
        </article>
        <article>
          <span>€/m² (cierre)</span>
          <strong>{num(result.perM2.final)} €</strong>
        </article>
        <article>
          <span>€/m² homogeneizado (oferta)</span>
          <strong>{num(result.perM2.adjusted)} €</strong>
        </article>
        <article>
          <span>Margen de negociación</span>
          <strong>
            {String(result.negotiationPct).replace(".", ",")} %{" "}
            <small>{result.negotiationSource === "manual" ? "manual" : "estimado"}</small>
          </strong>
        </article>
        <article className={`val-confidence val-confidence-${result.confidence.level}`}>
          <span>Fiabilidad</span>
          <strong>
            {result.confidence.level} · {result.confidence.score}/100
          </strong>
          <small>{result.confidence.reasons.join(" · ")}</small>
        </article>
        <article>
          <span>Testigos</span>
          <strong>
            {result.stats.used} de {result.stats.totalListings}
          </strong>
          <small>
            Radio {search?.radiusKm ? `${search.radiusKm} km` : "—"} · mediana {num(result.stats.medianDistance)} m
          </small>
        </article>
      </div>
      <div className="val-zone">
        <span>Oferta de la zona</span>
        <p>
          {num(result.zone.listings)} anuncios · mediana {num(result.zone.medianPerM2)} €/m² (rango {num(result.zone.p25PerM2)} –{" "}
          {num(result.zone.p75PerM2)}) · {Math.round(result.zone.priceDropShare * 100)} % con bajada de precio
          {result.zone.avgPriceDropPct ? ` (media ${result.zone.avgPriceDropPct.toFixed(1).replace(".", ",")} %)` : ""}
        </p>
      </div>
    </section>
  );
}

function ComparablesTable({ result, excludedIds, onToggle, disabled }) {
  const [filter, setFilter] = useState("used");
  const rows = result.comparables.filter((item) =>
    filter === "used" ? item.used : filter === "eligible" ? item.eligible : true
  );
  const counts = {
    used: result.comparables.filter((item) => item.used).length,
    eligible: result.comparables.filter((item) => item.eligible).length,
    all: result.comparables.length
  };

  return (
    <section className="admin-panel-section">
      <div className="admin-section-heading">
        <div>
          <p>Testigos</p>
          <h2>Inmuebles comparables</h2>
        </div>
        <div className="val-tabs">
          {[
            ["used", "Usados"],
            ["eligible", "Elegibles"],
            ["all", "Todos"]
          ].map(([key, label]) => (
            <button key={key} type="button" className={filter === key ? "active" : ""} onClick={() => setFilter(key)}>
              {label} <em>{counts[key]}</em>
            </button>
          ))}
        </div>
      </div>
      <div className="val-table-wrap">
        <table className="val-table">
          <thead>
            <tr>
              <th aria-label="Usar" />
              <th>Inmueble</th>
              <th>Dist.</th>
              <th>m²</th>
              <th>Planta</th>
              <th>Estado</th>
              <th>Precio</th>
              <th>€/m²</th>
              <th>€/m² homog.</th>
              <th>Notas</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((item) => {
              const excluded = excludedIds.has(item.id);
              const canToggle = item.eligible;
              return (
                <tr key={item.id} className={item.used ? "used" : "unused"}>
                  <td>
                    {canToggle && (
                      <button
                        type="button"
                        className={`val-toggle ${excluded ? "off" : "on"}`}
                        disabled={disabled}
                        title={excluded ? "Volver a incluir" : "Excluir de la valoración"}
                        onClick={() => onToggle(item.id)}
                      >
                        {excluded ? <Plus size={13} /> : <Check size={13} />}
                      </button>
                    )}
                  </td>
                  <td className="val-table-property">
                    {item.thumbnail && <img src={item.thumbnail} alt="" loading="lazy" />}
                    <div>
                      <a href={item.url} target="_blank" rel="noreferrer">
                        {item.address || item.neighborhood || "Ver anuncio"} <ExternalLink size={11} />
                      </a>
                      <span>
                        {[item.rooms && `${item.rooms} hab.`, item.bathrooms && `${item.bathrooms} baños`, item.exterior === false && "interior", item.parkingIncluded && "garaje", item.terrace && "terraza"]
                          .filter(Boolean)
                          .join(" · ")}
                      </span>
                    </div>
                  </td>
                  <td>{item.distance !== null ? `${num(item.distance)} m` : "—"}</td>
                  <td>{num(item.size)}</td>
                  <td>
                    {floorLabel(item.floorRaw) || "—"}
                    {item.hasLift === false ? " s/a" : ""}
                  </td>
                  <td>{CONDITION_LABELS[item.condition] || "—"}</td>
                  <td>
                    {euro(item.price)}
                    {item.priceDropPct ? <small className="val-drop">−{item.priceDropPct} %</small> : null}
                  </td>
                  <td>{num(item.pricePerM2)}</td>
                  <td className="val-table-adjusted">{item.adjustedPerM2 ? num(item.adjustedPerM2) : "—"}</td>
                  <td className="val-table-note">{item.used ? `peso ${item.weight.toFixed(2)}` : item.excludedReason || ""}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function SubjectSummary({ subject }) {
  const yesNo = (value) => (value === true ? "Sí" : value === false ? "No" : "—");
  const items = [
    ["Referencia catastral", subject.refCatastral || "—"],
    ["Tipología", PROPERTY_TYPES.find(([key]) => key === subject.propertyType)?.[1]],
    ["Superficie construida", subject.builtArea ? `${num(subject.builtArea)} m²` : "—"],
    ["Superficie útil", subject.usableArea ? `${num(subject.usableArea)} m²` : "—"],
    ["Dormitorios / baños", `${subject.rooms ?? "—"} / ${subject.bathrooms ?? "—"}`],
    ["Planta", subject.propertyType === "casa" ? "—" : subject.floor ?? "—"],
    ["Ascensor", yesNo(subject.hasLift)],
    ["Exterior", yesNo(subject.exterior)],
    ["Estado", CONDITION_LABELS[subject.condition]],
    ["Año", subject.yearBuilt || "—"],
    ["Certificado energético", subject.energyRating || "—"],
    [
      "Extras",
      [subject.garage && "garaje", subject.storage && "trastero", subject.terrace && "terraza", subject.pool && "piscina", subject.airConditioning && "A/A"]
        .filter(Boolean)
        .join(", ") || "—"
    ]
  ];
  return (
    <>
      <dl className="val-summary">
        {items.map(([label, value]) => (
          <div key={label}>
            <dt>{label}</dt>
            <dd>{value}</dd>
          </div>
        ))}
      </dl>
      {subject.agentNotes && <p className="val-notes">{subject.agentNotes}</p>}
    </>
  );
}

function AiEditor({ ai, aiError, dirty, saving, onChange, onSave, onRegenerate }) {
  const sections = [
    ["resumen", "Resumen de la valoración", 6],
    ["analisisZona", "La zona", 4],
    ["analisisMercado", "Oferta actual en la zona", 6],
    ["justificacionPrecio", "Justificación del valor", 6],
    ["estrategia", "Estrategia comercial", 6]
  ];
  return (
    <section className="val-card">
      <div className="val-card-heading">
        <div>
          <h3>Textos del informe</h3>
          <p>{ai ? `Redactados por ${ai.model || "IA"}${ai.editedAt ? " · editados por el agente" : ""}` : "Sin análisis de IA todavía."}</p>
        </div>
        <div className="val-card-heading-actions">
          <button type="button" className="val-btn" onClick={onRegenerate}>
            <Sparkles size={15} /> {ai ? "Regenerar con IA" : "Generar con IA"}
          </button>
          {ai && (
            <button type="button" className="val-btn val-btn-primary" disabled={!dirty || saving} onClick={onSave}>
              <Save size={15} /> Guardar textos
            </button>
          )}
        </div>
      </div>
      {aiError && (
        <p className="val-hint val-hint-warn">
          <AlertTriangle size={14} /> {aiError}
        </p>
      )}
      {ai && (
        <div className="val-ai">
          {sections.map(([key, label, rows]) => (
            <label key={key} className="val-field val-field-wide">
              <span>{label}</span>
              <textarea rows={rows} value={ai[key] || ""} onChange={(event) => onChange({ ...ai, [key]: event.target.value })} />
            </label>
          ))}
          <div className="val-grid val-grid-2">
            {[
              ["puntosFuertes", "Puntos fuertes (uno por línea)"],
              ["puntosMejora", "Aspectos a tener en cuenta (uno por línea)"]
            ].map(([key, label]) => (
              <label key={key} className="val-field">
                <span>{label}</span>
                <textarea
                  rows={6}
                  value={(ai[key] || []).join("\n")}
                  onChange={(event) => onChange({ ...ai, [key]: event.target.value.split("\n") })}
                />
              </label>
            ))}
          </div>
          <TextField label="Plazo de venta estimado" wide value={ai.plazoVenta} onChange={(value) => onChange({ ...ai, plazoVenta: value })} />
        </div>
      )}
    </section>
  );
}
